import dayjs from "dayjs";

interface CameraDailyCacheEntry<T> {
  data: T;
  fetchedDate: string;
  requestedDay: string;
}

const DATE_FORMAT = "YYYY-MM-DD";

const getDateFromTimestamp = (timestamp: string): string => {
  return dayjs(timestamp).utc().format(DATE_FORMAT);
};

const getTodayUTC = (): string => {
  return dayjs().utc().format(DATE_FORMAT);
};

const getCacheKey = (
  cacheKeyPrefix: string,
  timestamp: string,
  storeKey?: string
): string => {
  const requestedDay = getDateFromTimestamp(timestamp);
  const storeKeySegment = storeKey ? `:${storeKey}` : "";
  return `${cacheKeyPrefix}:${requestedDay}${storeKeySegment}`;
};

export const createCameraDailyCache = <T>(cacheKeyPrefix: string) => {
  const clearExpiredCache = (): void => {
    const todayUTC = getTodayUTC();

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(cacheKeyPrefix)) {
        const cacheEntry: CameraDailyCacheEntry<T> = JSON.parse(
          localStorage.getItem(key) || "{}"
        );

        if (cacheEntry.fetchedDate < todayUTC) {
          localStorage.removeItem(key);
        }
      }
    });
  };

  const get = (timestamp: string, storeKey?: string): T | null => {
    const cacheKey = getCacheKey(cacheKeyPrefix, timestamp, storeKey);

    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData) {
      const { data } = JSON.parse(cachedData) as CameraDailyCacheEntry<T>;
      return data;
    }

    return null;
  };

  const set = (requestTimestamp: string, data: T, storeKey?: string): void => {
    const cacheKey = getCacheKey(cacheKeyPrefix, requestTimestamp, storeKey);
    const cacheEntry: CameraDailyCacheEntry<T> = {
      data,
      fetchedDate: getTodayUTC(),
      requestedDay: getDateFromTimestamp(requestTimestamp),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  };

  return {
    clearExpiredCache,
    get,
    set,
  };
};
